/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Tabs, Tab } from '@mui/material';
import React from 'react';
import { Notifications } from '@opengeoweb/theme';
import type { SWEvent } from '../../types';
import { EventCategory } from '../../types';
import { useSpaceweatherTranslation } from '../../utils/i18n';

interface NotificationTabsProps {
  activeTab: string;
  newNotifications: SWEvent[];
  onChange: (value: string) => void;
}

const NotificationTabs: React.FC<NotificationTabsProps> = ({
  activeTab,
  newNotifications,
  onChange,
}: NotificationTabsProps) => {
  const { t } = useSpaceweatherTranslation();
  const icon =
    newNotifications !== null && newNotifications.length > 0 ? (
      <Notifications
        color="warning"
        data-testid="newNotificationTabIcon"
        fontSize="small"
      />
    ) : null;

  return (
    <Tabs
      indicatorColor="secondary"
      onChange={(event: React.SyntheticEvent, value): void => {
        onChange(value);
      }}
      scrollButtons="auto"
      textColor="secondary"
      value={activeTab}
      variant="scrollable"
    >
      <Tab
        data-testid="newNotificationTabAll"
        icon={icon!}
        label={t('notification-tab-all')}
        sx={{
          width: 'auto',
          padding: 0,
          flexDirection: 'row-reverse',
          minHeight: '48px',
          '&.MuiButtonBase-root.MuiTab-root > .MuiTab-iconWrapper': {
            marginBottom: '0',
          },
        }}
        value="ALL"
      />
      {Object.keys(EventCategory).map((key: string) => (
        <Tab
          key={key}
          label={EventCategory[key as keyof typeof EventCategory]}
          value={key}
        />
      ))}

      <Tab data-testid="newNotificationTabKNMI" label="KNMI" value="KNMI" />
    </Tabs>
  );
};

export default NotificationTabs;
